import React, { useState } from 'react';
import { useLiveSports, type UseLiveSportsReturn } from '../hooks/useLiveSports';
import type { LiveGame, Team } from '../types/index';

function formatKickoff(kickoff: string): string {
  const date = new Date(kickoff);
  if (Number.isNaN(date.getTime())) return 'Time TBD';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function TeamBadge({ team }: { team: Team }) {
  const [failed, setFailed] = useState(false);
  const initials = team.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (!team.logo || failed) {
    return <span className="score-ticker__badge">{initials || '?'}</span>;
  }

  return (
    <img
      src={team.logo}
      alt={`${team.name} logo`}
      loading="lazy"
      className="score-ticker__logo"
      onError={() => setFailed(true)}
    />
  );
}

function GameLabel({ game }: { game: LiveGame }) {
  const detail =
    game.status === 'final' && game.awayScore !== undefined && game.homeScore !== undefined
      ? `Final ${game.awayScore}-${game.homeScore}`
      : formatKickoff(game.kickoff);

  return (
    <div className="score-ticker__item">
      <span className="score-ticker__league">{game.league}</span>
      <span className="score-ticker__teams">
        <TeamBadge team={game.awayTeam} />
        {game.awayTeam.name}
        <span className="score-ticker__at">@</span>
        <TeamBadge team={game.homeTeam} />
        {game.homeTeam.name}
      </span>
      <span className="score-ticker__detail">{detail}</span>
    </div>
  );
}

interface ScoreTickerProps {
  liveSports?: UseLiveSportsReturn;
}

function ScoreTickerInner({ liveSports }: { liveSports: UseLiveSportsReturn }) {
  const { tickerItems } = liveSports;
  const [paused, setPaused] = useState(false);

  if (tickerItems.length === 0) return null;

  const loopItems = [...tickerItems, ...tickerItems];

  return (
    <section
      aria-label="Live and recent sports scores"
      className="score-ticker"
      tabIndex={0}
      data-paused={paused ? 'true' : undefined}
      onPointerDown={(event) => {
        if (event.pointerType !== 'mouse') setPaused(true);
      }}
      onPointerUp={() => setPaused(false)}
      onPointerCancel={() => setPaused(false)}
      onPointerLeave={() => setPaused(false)}
    >
      <div className="score-ticker__track">
        {loopItems.map((game, index) => (
          <GameLabel key={`${game.id}-${index}`} game={game} />
        ))}
      </div>
    </section>
  );
}

const ScoreTicker: React.FC<ScoreTickerProps> = ({ liveSports }) => {
  if (liveSports) return <ScoreTickerInner liveSports={liveSports} />;
  return <ScoreTickerStandalone />;
};

function ScoreTickerStandalone() {
  const liveSports = useLiveSports();
  return <ScoreTickerInner liveSports={liveSports} />;
}

export default ScoreTicker;
